/**
 * Every competition-scoped cache entry is keyed by its `competitionId`.
 *
 * This is not a tidiness preference. Without the id in the key, switching
 * competitions in the UI would serve competition A's teams under competition
 * B's screen — the client-side twin of the IDOR the server refuses. Keys are
 * built here so no screen has to remember.
 */
export const competitionKeys = {
  all: ['competitions'] as const,
  list: () => [...competitionKeys.all, 'list'] as const,
  detail: (competitionId: string) => [...competitionKeys.all, competitionId] as const,
  categories: (competitionId: string) =>
    [...competitionKeys.detail(competitionId), 'categories'] as const,
  discoveryCategories: (competitionId: string) =>
    [...competitionKeys.detail(competitionId), 'discovery-categories'] as const,
  rounds: (competitionId: string) => [...competitionKeys.detail(competitionId), 'rounds'] as const,
  roundAdvancements: (competitionId: string, roundId: string) =>
    [...competitionKeys.rounds(competitionId), roundId, 'advancements'] as const,
  myRoundAdvancement: (competitionId: string, roundId: string) =>
    [...competitionKeys.rounds(competitionId), roundId, 'advancements', 'me'] as const,
  roles: (competitionId: string) => [...competitionKeys.detail(competitionId), 'roles'] as const,
  teams: (competitionId: string, filters: Record<string, string> = {}) =>
    [...competitionKeys.detail(competitionId), 'teams', filters] as const,
  teamDetail: (competitionId: string, teamId: string) =>
    [...competitionKeys.detail(competitionId), 'teams', teamId] as const,
  teamInvites: (competitionId: string, teamId: string) =>
    [...competitionKeys.teamDetail(competitionId, teamId), 'invites'] as const,
  settings: (competitionId: string) =>
    [...competitionKeys.detail(competitionId), 'settings'] as const,
  auditLogs: (competitionId: string, filters: Record<string, string> = {}) =>
    [...competitionKeys.detail(competitionId), 'audit-logs', filters] as const,
  search: (competitionId: string, q: string) =>
    [...competitionKeys.detail(competitionId), 'search', q] as const,
  announcements: (competitionId: string) =>
    [...competitionKeys.detail(competitionId), 'announcements'] as const,
}
